import { getApiUrl } from "../api";
import { useEffect, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { ArabicHeader, ArabicFooter } from "./ArabicHome";

const getJwtToken = () => {
  const keys = ["token", "accessToken", "jwt", "authToken"];
  for (const key of keys) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token?.trim()) return token.replace(/^Bearer\s+/i, "").trim();
  }

  return (process.env.REACT_APP_JWT_TOKEN || "").replace(/^Bearer\s+/i, "").trim();
};


const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const mapDirector = (item, index, language = "en") => {
  const image = getCloudflareImageUrl(item.ImagePath || item.image || item.imagePath, "w=700,sharpen=1");

  return {
    id: item.id || item.Id || `director-${index}`,
    name: stripHtml(language === "ar" ? item.name_ar || item.name || item.title_ar || item.name_en || item.title_en : item.name_en || item.name || item.title_en),
    designation: stripHtml(language === "ar" ? item.designation_ar || item.designation || item.description_ar || item.description_en : item.designation_en || item.designation || item.description_en),
    image: image,
  };
};

export default function Directors({ language = "en" }) {
  const interactions = useHomeInteractions();
  const [directors, setDirectors] = useState([]);

  useEffect(() => {
    document.title = "Board of Directors | Tarahum Charity Foundation";
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadDirectors = async () => {
      try {
        const headers = { Accept: "application/json" };
        const token = getJwtToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(getApiUrl("/api/GetBoardofDirectorsList.ashx?page=1&pageSize=200"), {
          headers,
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Board directors request failed with ${response.status}`);
        }

        const result = await response.json();
        const rows = Array.isArray(result?.data) ? result.data : [];
        const mappedRows = rows.map((item, index) => mapDirector(item, index, language)).filter((item) => item.name);

        if (mappedRows.length) {
          setDirectors(mappedRows);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load board directors", error);
        }
      }
    };

    loadDirectors();

    return () => controller.abort();
  }, [language]);

  useEffect(() => {
    if (language !== "ar") return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => { document.body.classList.remove("arabic-layout-page"); document.documentElement.lang = "en"; document.documentElement.dir = "ltr"; };
  }, [language]);

  return (
    <>
      <Preloader />
      {language === "ar" ? <ArabicHeader /> : <Header />}
      {language === "ar" ? null : <LanguagePopup />}

      <section className="kid-d-wrp pb-0">
        <div className="kid-container">
          <div className="kid-hero">
            <h1>{language === "ar" ? "مجلس الإدارة" : "Board of Directors"}</h1>
            <ul className="kid-breadcrumb">
              <li>
                <Link to="/">{language === "ar" ? "الرئيسية" : "home"}</Link>
              </li>
              <li>/</li>
              <li>{language === "ar" ? "مجلس الإدارة" : "Board of Directors"}</li>
            </ul>
          </div>
        </div>
      </section>

      <div className="page-team directors-modern">
        <div className="container">
          <div className="row">
            {directors.map((director) => (
              <div className="col-xl-4 col-md-6 col-6" key={director.id || director.name}>
                <div className="team-item wow fadeInUp" style={{ visibility: "visible", animationName: "fadeInUp" }}>
                  <div className="team-item-image">
                    <figure>
                      <img
                        src={director.image}
                        alt={director.name}
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    </figure>
                  </div>
                  <div className="team-item-body">
                    <div className="team-item-content">
                      <h2>
                        <span>{director.name}</span>
                      </h2>
                      {director.designation ? <p>{director.designation}</p> : null}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {language === "ar" ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
